import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOnlineOrderDetailPageQuery } from '@app/page/online-order/detail/logic/use-online-order-detail-page-query';
import useOnlineOrderUpdateSubscription from '@app/page/online-order/detail/logic/use-online-order-update-subscription';
import { getFragmentData } from '@app/graphql/types';
import { OnlineOrderFragment } from '@app/page/online-order/model/online-order-fragment';
import { OnlineOrderUserState } from '@app/graphql/types/graphql';
import { captureException } from '@sentry/react';
import { useTranslate } from '@tolgee/react';
import Spinner from '@app/components/spinner';
import ApplicationErrorView from '@app/module/error/application-error-view';
import Loading from '@app/components/loading';
import { useOnlineOrderDetailStore } from '@app/page/online-order/detail/logic/use-online-order-detail-store';
import { ApolloError } from '@apollo/client';
import { useOnlineOrderWaitingPaymentCheck } from './logic/use-online-order-waiting-payment-check';
import SuperButton from '@app/components/super-button';

export const OnlineOrderSuccessPage: React.FC = () => {
    const { onlineOrderID } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslate();
    const lastUpdateTimestamp = useOnlineOrderDetailStore((state) => state.lastUpdateTimestamp);
    const [checkPayment, { loading: checkPaymentLoading }] = useOnlineOrderWaitingPaymentCheck();

    const { data, loading, error, refetch } = useOnlineOrderDetailPageQuery(onlineOrderID);
    const { data: updateStreamData } = useOnlineOrderUpdateSubscription(onlineOrderID);

    const onlineOrder = getFragmentData(OnlineOrderFragment, data?.storefront_onlineOrder);

    useEffect(() => {
        if (onlineOrderID) {
            refetch().catch(captureException);
        }
    }, [onlineOrderID, refetch, updateStreamData, lastUpdateTimestamp]);

    useEffect(() => {
        if (onlineOrder?.userState === OnlineOrderUserState.Done || onlineOrder?.userState === OnlineOrderUserState.Review) {
            if (onlineOrderID) {
                navigate(`/online-order/${onlineOrderID}`);
            }
        }
    }, [onlineOrder?.userState, onlineOrderID, navigate]);

    const handleRefresh = () => {
        if (onlineOrderID) {
            checkPayment({
                variables: {
                    onlineOrderID,
                },
            }).catch(captureException);
        }
    };

    if (loading) {
        return <Loading />;
    }

    if (error) {
        return <ApplicationErrorView error={error} />;
    }

    if (!onlineOrder) {
        return <ApplicationErrorView error={new ApolloError({})} />;
    }

    return (
        <div className="payment-page flex flex-col items-center justify-center min-h-screen p-4">
            <div className="payment-page__container max-w-md w-full bg-white rounded-lg shadow-md p-6">
                <h1 className="payment-page__title text-2xl font-bold text-center mb-4">{t('online-order.payment.success.title', 'Payment Successful')}</h1>
                <p className="payment-page__message text-center mb-6 text-gray-600">
                    {t('online-order.payment.success.description', 'Your payment has been processed successfully. You will be redirected to your order details shortly.')}
                </p>
                <div className="payment-page__actions flex flex-col items-center gap-4">
                    <div className="payment-page__spinner flex justify-center">
                        <Spinner />
                    </div>
                    <SuperButton
                        className="payment-page__refresh-btn w-full"
                        onClick={handleRefresh}
                        disabled={checkPaymentLoading}
                    >
                        {t('online-order.payment.success.refresh', 'Refresh')}
                    </SuperButton>
                </div>
            </div>
        </div>
    );
}; 